import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  LinearProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Alert,
  Button
} from "@mui/material";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const ProcessingStep = ({
  rows = {},
  progress = {},
  totalRows = 0,
  loading,
  result,
  onStartNewUpload,
  onRetryCredentials,
  configValues = {},
  processError = null,
  socketConnected = true,
  interrupted = false,
}) => {

  const rowIndex = progress?.rowIndex ?? 0;

  const isSystemPhase = rowIndex === 0;

  const overallPercent =
    totalRows > 0 && rowIndex > 0
      ? Math.round((rowIndex / totalRows) * 100)
      : 0;

  const stepPercent =
    progress?.phase?.index !== undefined && progress?.phase?.total
      ? Math.round((progress.phase.index / progress.phase.total) * 100)
      : 0;

  const sortedRows = Object.entries(rows)
    .filter(([, row]) => row.fileNo || row.fipId)
    .sort((a, b) => Number(a[0]) - Number(b[0]));

  const getStatusChip = (status) => {

    if (status === "file_completed")
      return <Chip label="Completed" color="success" size="small" />;

    if (status === "file_skipped")
      return <Chip label="Skipped" color="warning" size="small" />;

    if (status === "file_failed")
      return <Chip label="Failed" color="error" size="small" />;

    if (status === "file_started")
      return <Chip label="Processing" color="info" size="small" />;

    return <Chip label="Pending" size="small" />;
  };

  const successCount = result?.successProcessingList?.length || 0;
  const failureCount = result?.failureProcessingList?.length || 0;

  return (

    <Box>

      <Paper sx={{ p: 4 }}>

        {interrupted && (
          <Alert
            icon={<WarningAmberIcon fontSize="inherit" />}
            severity="error"
            sx={{ mb: 3 }}
          >
            Job interrupted — the server restarted mid-process. Progress shown above may be incomplete
            and no report is available for this run. Please start a new upload.
          </Alert>
        )}

        {!interrupted && loading && !socketConnected && (
          <Alert
            icon={<WifiOffIcon fontSize="inherit" />}
            severity="warning"
            sx={{ mb: 3 }}
          >
            Reconnecting… your job is still running on the server.
          </Alert>
        )}

        <Typography variant="h3" sx={{ mb: 3 }}>
          Records Data
        </Typography>

        {/* OVERALL PROGRESS */}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 4
          }}
        >

          {loading && (isSystemPhase ? (

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: 2
              }}
            >

              <CircularProgress size={18} thickness={5} />

              <Typography variant="h5">
                {progress?.phase?.label
                  ? `${progress.phase.label}...`
                  : "Preparing automation..."}
              </Typography>

            </Box>

          ) : (

            <>

              {/* Circular progress with percentage overlaid */}
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress
                  variant="determinate"
                  value={overallPercent}
                  size={90}
                  thickness={4}
                  sx={{ color: "primary.main" }}
                />
                <Box
                  sx={{
                    top: 0, left: 0, bottom: 0, right: 0,
                    position: "absolute",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: "primary.main" }}>
                    {overallPercent}%
                  </Typography>
                </Box>
              </Box>

              <Typography sx={{ mt: 2, fontWeight: 500 }}>
                {rowIndex} of {totalRows} records processed
              </Typography>

            </>

          ))}

        </Box>

        {/* CURRENT STEP */}

        {!isSystemPhase && progress?.phase && loading && (

          <Box sx={{ mb: 4 }}>

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {progress.phase.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Step {progress.phase.index} / {progress.phase.total}
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={stepPercent}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: "grey.200",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 5
                }
              }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              {stepPercent}% complete
            </Typography>

          </Box>

        )}

        {/* PROCESS ERROR */}

        {processError && (
          <Alert severity="error" variant="outlined" sx={{ mb: 4 }}>
            {processError}
          </Alert>
        )}

        {/* FINAL RESULT SUMMARY */}

        {!loading && result && (

          <Box sx={{ mb: 4 }}>

            {successCount > 0 && (

              <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
                {successCount} records successfully processed.
              </Alert>

            )}

            {failureCount > 0 && (

              <Alert severity="error" variant="outlined">
                {failureCount} records skipped
              </Alert>

            )}

          </Box>

        )}

        {/* LIVE ROW TABLE */}

        <Table size="small">

          <TableHead>

            <TableRow>
              <TableCell>Row</TableCell>
              <TableCell>{configValues.action === "update" ? "FIP Id" : "File No"}</TableCell>
              <TableCell>Current Step</TableCell>
              <TableCell>Remarks</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>

          </TableHead>

          <TableBody>

            {sortedRows.map(([rowIndex, row]) => (

              <TableRow key={rowIndex}>

                <TableCell>{rowIndex}</TableCell>

                <TableCell>{configValues.action === "update" ? row.fipId : row.fileNo}</TableCell>

                <TableCell>{row.phase?.label ?? row.phase}</TableCell>

                <TableCell>{row.remarks || ""}</TableCell>

                <TableCell>
                  {getStatusChip(row.status)}
                </TableCell>

              </TableRow>

            ))}

          </TableBody>

        </Table>

        {/* ACTION BUTTONS */}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 4
          }}
        >

          {/* <Button
            variant="text"
            disabled={loading}
          >
            View Log
          </Button> */}

          <Button
            variant="outlined"
            onClick={onStartNewUpload}
          >
            Start New Upload
          </Button>

          {onRetryCredentials && (
            <Button
              variant="contained"
              color="primary"
              disabled={loading}
              onClick={onRetryCredentials}
            >
              Re-enter Credentials
            </Button>
          )}

          <Button
            variant="contained"
            disabled={loading || !result?.fileUrl}
            onClick={() => {
              const a = document.createElement("a");
              a.href = result.fileUrl;
              a.download = "report.xlsx";
              a.click();
            }}
          >
            Download Report
          </Button>

        </Box>

      </Paper>

    </Box>

  );
};

export default ProcessingStep;